<script setup lang="ts">
import { Drawer, Button } from 'primevue';
import { useSelectedLogSorterStore, type Bin } from '../stores/selectedLogSorterStore';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import LogSorterPanel from './LogSorterPanel.vue';
import { useUpdateLogSorter, type LogSorter, type UpdateLogSorterRequest } from '@/logSorter/api/updateLogSorter';
import { Assert } from 'shared/assert';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useCreateLogSorter, type CreateLogSorterRequest } from '@/logSorter/api/createLogSorter';
import type { LogSorterBin } from '@/logSorter/api/common/logSorterBin';
import { useUpdateShape, type UpdateShapeRequest } from '@/shapes/api/updateShape';
import { useCreateShape, type CreateShapeRequest } from '@/shapes/api/createShape';
import { useCreateStorageArea, type CreateStorageAreaRequest } from '@/storageAreas/api/createStorageArea';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { useQueryShape } from '@/shapes/api/queryShape';
import { useDeleteStorageArea } from '@/storageAreas/api/deleteStorageArea';
import { useDeleteLogSorterConfirm } from '@/logSorter/composables/deleteLogSorterConfirm';
import LogSorterBinsPanel from './LogSorterBinsPanel.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';

const store = useSelectedLogSorterStore();
const active = ref<boolean>(false);
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const actionFailedAlert = useActionFailedAlert();

const createLogSorter = useCreateLogSorter();
const updateLogSorter = useUpdateLogSorter();

const createShape = useCreateShape();
const updateShape = useUpdateShape();
const queryShape = useQueryShape();

const createStorageArea = useCreateStorageArea();
const deleteStorageArea = useDeleteStorageArea();

const loading = ref<boolean>(false);

const activeTab = ref<'logSorter' | 'bins'>('logSorter');

const {
  severity: logSorterTabSeverity,
  variant: logSorterTabVariant
} = tabState('logSorter');

const {
  severity: binsTabSeverity,
  variant: binsTabVariant
} = tabState('bins');

const deleteLogSorterConfirm = useDeleteLogSorterConfirm();

const {
  name,
  operation,
  changed,
  invalid
} = storeToRefs(store);

const saveDisabled = computed(() => {

  if (loading.value) return true;
  if (invalid.value) return true;

  switch (operation.value) {
    case 'create':
      return false;
    case 'edit':
      return !changed.value;
    default:
      return true;
  }
});

const deleteDisabled = computed(() => {
  switch (operation.value) {
    case 'edit':
      return false;
  }

  return true;
});


watch(operation, (newOperation) => {
  active.value = newOperation !== 'none';
});

const title = computed(() => {
  if (name.value === undefined && operation.value === 'create') {
    return translations.value.logSorter.drawer.createTitle;
  }
  return name.value;
});

async function onSaveClick() {

  if (operation.value === 'edit') {
    await update();
  } else if (operation.value === 'create') {
    await create();
  }
}

async function create() {
  try {
    loading.value = true;
    const {
      name,
      addLogWhenSorted,
      configuration,
      bins,
      intakes
    } = store;

    Assert.definied(name);

    const logSorterBins: LogSorterBin[] = [];

    for (const bin of bins) {
      const createdBin = await createBin(bin);
      if (createdBin === undefined) return;
      logSorterBins.push(createdBin);
    }

    const request: CreateLogSorterRequest = {
      logSorter: {
        name: name,
        addLogWhenSorted: addLogWhenSorted,
        bins: logSorterBins,
        intakes: intakes,
        configuration: JSON.stringify(configuration),
      }
    };
    console.log("request", request);

    const response = await createLogSorter.execute(request);

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    store.edit({
      logSorter: response.logSorter
    });
  } finally {
    loading.value = false;
  }
}

async function update() {
  try {
    loading.value = true;
    const {
      logSorterId,
      name,
      addLogWhenSorted,
      configuration,
      bins,
      deleteStorageAreaIds,
      intakes
    } = store;

    Assert.definied(logSorterId);
    Assert.definied(name);

    const logSorterBins: LogSorterBin[] = [];

    for (const bin of bins) {
      const storageAreaId = bin.storageAreaId;

      if (storageAreaId) {

        const shapeResponse = await queryShape.execute({
          storageAreaId: storageAreaId
        });

        if (!shapeResponse.ok) {
          actionFailedAlert.show(shapeResponse.details);
          return;
        }

        if (shapeResponse.shapes.length > 0) {
          const shape = shapeResponse.shapes[0];
          if (shape) {
            const shapeId = shape.id;
            const shapeVersion = shape.version;
            const updatedBin = await updateBin({
              bin,
              shapeId,
              storageAreaId,
              shapeVersion
            });
            if (updatedBin === undefined) return;
            logSorterBins.push(updatedBin);
          }
        }

      } else {
        const createdBin = await createBin(bin);
        if (createdBin === undefined) return;
        logSorterBins.push(createdBin);
      }
    }

    for (const storageAreaId of deleteStorageAreaIds) {
      const deleteResponse = await deleteStorageArea.execute({ storageAreaId });
      if (!deleteResponse.ok) {
        actionFailedAlert.show(deleteResponse.details);
        return;
      }
    }

    const logSorter: LogSorter = {
      name: name,
      addLogWhenSorted: addLogWhenSorted,
      bins: logSorterBins,
      intakes: intakes,
      configuration: JSON.stringify(configuration),
    };

    const request: UpdateLogSorterRequest = {
      id: logSorterId,
      logSorter: logSorter,
    };

    const response = await updateLogSorter.execute(request);

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    store.edit({
      logSorter: response.logSorter
    });
  } finally {
    loading.value = false;
  }
}

async function createBin(bin: Bin): Promise<LogSorterBin | undefined> {
  const storageAreaRequest: CreateStorageAreaRequest = {
    storageArea: {
      active: true,
      name: bin.name,
      temporary: false,
      status: StorageAreaStatus.Empty,
      type: StorageAreaType.LogSorterBin,
      version: crypto.randomUUID(),
      allowedProducts: [],
      logYardData: {
        allowMultipleProducts: false,
        disableAutomaticProductChange: false,
        sideArea: 0
      },
      coilStack: null,
      bulkStorageData: null,
      note: null,
      importantNote: false,
    }
  };

  const storageAreaResponse = await createStorageArea.execute(storageAreaRequest);

  if (!storageAreaResponse.ok) {
    actionFailedAlert.show(storageAreaResponse.details);
    return;
  }

  const shapeRequest: CreateShapeRequest = {
    shape: {
      shapeGeoJson: bin.geoJson.shape,
      geofenceGeoJson: bin.geoJson.geofence,
      storageAreaId: storageAreaResponse.storageArea.id,
      version: crypto.randomUUID(),
      color: null
    }
  };

  const shapeResponse = await createShape.execute(shapeRequest);

  if (!shapeResponse.ok) {
    actionFailedAlert.show(shapeResponse.details);
    return;
  }

  return {
    binIdentifier: bin.identifier,
    storageAreaId: storageAreaResponse.storageArea.id,
    binNumber: bin.number,
    width: bin.width,
    length: bin.length,
    offsetY: bin.offsetY,
    offsetX: bin.offsetX
  };
}

async function updateBin(action: UpdateBin): Promise<LogSorterBin | undefined> {

  const {
    bin,
    shapeId,
    storageAreaId,
    shapeVersion
  } = action;

  const shapeRequest: UpdateShapeRequest = {
    shapeId: shapeId,
    shape: {
      shapeGeoJson: bin.geoJson.shape,
      geofenceGeoJson: bin.geoJson.geofence,
      storageAreaId: storageAreaId,
      version: shapeVersion,
      color: null
    }
  };

  const shapeResponse = await updateShape.execute(shapeRequest);

  if (!shapeResponse.ok) {
    actionFailedAlert.show(shapeResponse.details);
    return;
  }

  return {
    binIdentifier: bin.identifier,
    storageAreaId: storageAreaId,
    binNumber: bin.number,
    width: bin.width,
    length: bin.length,
    offsetY: bin.offsetY,
    offsetX: bin.offsetX,
  };
}

async function onDeleteClick() {
  const {
    logSorterId,
  } = store;

  Assert.definied(logSorterId);

  if (await deleteLogSorterConfirm.show({ logSorterId })) {
    store.hide();
  }
}

function tabState(value: string) {
  return {
    severity: tabSeverity(value),
    variant: tabVariant(value)
  };
}

function tabSeverity(value: string) {
  return computed(() => activeTab.value === value ? 'primary' : undefined);
}

function tabVariant(value: string) {
  return computed(() => activeTab.value !== value ? 'outlined' : undefined);
}

interface UpdateBin {
  shapeId: string;
  storageAreaId: string;
  bin: Bin;
  shapeVersion: string;
}

</script>

<template>
  <Drawer v-model:visible="active" :header="title" position="right" :modal="false" :dismissable="false"
    @before-hide="store.hide()" class="logsorter-drawer">

    <div class="tabs">
      <Button :severity="logSorterTabSeverity" :variant="logSorterTabVariant" @click="activeTab = 'logSorter'">{{
        translations.logSorter.drawer.logSorterTab }}</Button>
      <Button :severity="binsTabSeverity" :variant="binsTabVariant" @click="activeTab = 'bins'">{{
        translations.logSorter.drawer.binsTab }}</Button>
    </div>

    <div class="body">
      <LogSorterPanel v-if="activeTab === 'logSorter'"></LogSorterPanel>
      <LogSorterBinsPanel v-if="activeTab === 'bins'"></LogSorterBinsPanel>
    </div>

    <div class="actions">

      <Button :disabled="saveDisabled" :loading="loading" class="save-button" :label="translations.generic.save"
        icon="pi pi-save" @click="onSaveClick"></Button>

      <Button :disabled="deleteDisabled" class="delete-button" severity="danger" :label="translations.generic.delete"
        icon="pi pi-trash" @click="onDeleteClick"></Button>

    </div>

  </Drawer>
</template>

<style scoped>
.tabs {
  display: flex;
  column-gap: .5rem;
}

.body {
  overflow-y: auto;
}

.actions {
  display: flex;
  column-gap: .5rem;
}
</style>

<style>
.logsorter-drawer {
  width: 650px !important;
}

.logsorter-drawer .p-drawer-content {
  row-gap: .5rem;
  display: grid;
  grid-template-rows: auto 1fr auto;
}
</style>
